import React, { useId, useState } from "react"

import {
  Button,
  focusOnMount,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
} from "@coval/ui"
import {
  ArrowLeft,
  Binary,
  BookOpen,
  Braces,
  ChevronRight,
  Hash,
  IdCard,
  ListChecks,
  List as ListIcon,
  Plus,
  Tag,
  ToggleLeft,
  Type,
  type LucideIcon,
} from "lucide-react"

import { useTestSetEditorContext } from "../../contexts/TestSetEditorContext"
import {
  METADATA_COLUMN_TYPE_LABELS,
  type MetadataColumnType,
} from "../../utils/metadataColumnType"
import type { ToggleableColumn } from "./TestCaseColumnVisibility"

const TYPE_OPTIONS: MetadataColumnType[] = [
  "string",
  "int",
  "float",
  "bool",
  "list",
  "dict",
]

const METADATA_TYPE_ICONS: Record<MetadataColumnType, LucideIcon> = {
  string: Type,
  int: Hash,
  float: Binary,
  bool: ToggleLeft,
  list: ListIcon,
  dict: Braces,
}

const STATIC_COLUMN_META: Record<
  ToggleableColumn,
  { label: string; icon: LucideIcon }
> = {
  id: { label: "ID", icon: Hash },
  type: { label: "Type", icon: Tag },
  description: { label: "Description", icon: IdCard },
  behaviors: { label: "Expected Behaviors", icon: ListChecks },
  metadata: { label: "Metadata", icon: Braces },
  kbEntries: { label: "Knowledge Base Entries", icon: BookOpen },
}

const STATIC_COLUMN_ORDER: ToggleableColumn[] = [
  "id",
  "type",
  "description",
  "behaviors",
  "metadata",
  "kbEntries",
]

interface AddColumnMenuProps {
  width: string
}

interface MenuRowProps {
  icon: LucideIcon
  label: string
  trailing?: React.ReactNode
  onPress: () => void
}

const MenuRow: React.FC<MenuRowProps> = ({
  icon: Icon,
  label,
  trailing,
  onPress,
}) => (
  <button
    type="button"
    onClick={onPress}
    className="flex items-center gap-3 w-full px-3 py-2 text-left text-sm text-coval-text hover:bg-surface-1 transition-colors rounded-none"
  >
    <Icon className="w-4 h-4 text-coval-text-muted shrink-0" />
    <span className="flex-1 truncate">{label}</span>
    {trailing ? (
      <span className="text-coval-text-muted text-xs shrink-0">{trailing}</span>
    ) : null}
  </button>
)

/**
 * Unified "+ Add Column" menu. Shows hidden built-in columns and hidden
 * metadata keys as clickable rows, with a "Create new attribute" row that
 * flips the panel to a typed form for adding a custom metadata column.
 */
export const AddColumnMenu: React.FC<AddColumnMenuProps> = ({ width }) => {
  const { visibleColumns, toggleColumn, metadataColumns, metadataColumnState } =
    useTestSetEditorContext()

  const nameInputId = useId()
  const typeSelectId = useId()
  const [isOpen, setIsOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [mode, setMode] = useState<"list" | "create">("list")
  const [key, setKey] = useState("")
  const [type, setType] = useState<MetadataColumnType>("string")

  const hiddenStatic = STATIC_COLUMN_ORDER.filter(
    (col) => !visibleColumns.has(col),
  )
  const hiddenMetadata = metadataColumns.filter((c) =>
    metadataColumnState.hiddenKeys.has(c.key),
  )
  const existingMetadataKeys = new Set(metadataColumns.map((c) => c.key))

  const trimmed = key.trim()
  const isDuplicate = trimmed !== "" && existingMetadataKeys.has(trimmed)
  const canSubmit = trimmed !== "" && !isDuplicate

  const resetAndClose = () => {
    setMode("list")
    setKey("")
    setType("string")
    setIsOpen(false)
  }

  const handleAddMetadata = () => {
    if (!canSubmit) {
      return
    }
    metadataColumnState.addColumn(trimmed, type)
    if (!visibleColumns.has("metadata")) {
      toggleColumn("metadata")
    }
    resetAndClose()
  }

  const handleShowStatic = (col: ToggleableColumn) => {
    toggleColumn(col)
    resetAndClose()
  }

  const handleShowMetadataKey = (columnKey: string) => {
    metadataColumnState.showColumn(columnKey)
    resetAndClose()
  }

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
        if (!open) {
          setMode("list")
        }
      }}
    >
      <PopoverTrigger asChild>
        <th
          className="relative py-3 text-left text-sm px-2 font-medium text-coval-text-muted !bg-surface-default cursor-pointer transition-colors hover:bg-coval-contrast/10 outline-none"
          style={{
            width,
            maxWidth: width,
            minWidth: width,
            verticalAlign: "middle",
            boxShadow: isFocused
              ? "inset 0 0 0 2px var(--coval-contrast)"
              : undefined,
          }}
          tabIndex={0}
          onClick={(e) => e.stopPropagation()}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              setIsOpen(true)
            }
          }}
          aria-label="Add column"
        >
          <span className="flex items-center gap-1 whitespace-nowrap">
            <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
            Add Column
          </span>
        </th>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="p-0 w-72 rounded-md overflow-hidden"
      >
        {mode === "list" ? (
          <div className="flex flex-col w-full py-1">
            {hiddenStatic.map((col) => {
              const meta = STATIC_COLUMN_META[col]
              return (
                <MenuRow
                  key={`static:${col}`}
                  icon={meta.icon}
                  label={meta.label}
                  onPress={() => handleShowStatic(col)}
                />
              )
            })}
            {hiddenMetadata.map((c) => {
              const effectiveType =
                metadataColumnState.overrides[c.key] ?? c.inferredType
              const Icon = METADATA_TYPE_ICONS[effectiveType]
              return (
                <MenuRow
                  key={`meta:${c.key}`}
                  icon={Icon}
                  label={c.key}
                  trailing={METADATA_COLUMN_TYPE_LABELS[effectiveType]}
                  onPress={() => handleShowMetadataKey(c.key)}
                />
              )
            })}
            {hiddenStatic.length + hiddenMetadata.length > 0 ? (
              <div className="border-t border-coval-border my-1" />
            ) : null}
            <MenuRow
              icon={Plus}
              label="Create new attribute"
              trailing={
                <ChevronRight className="w-4 h-4 text-coval-text-muted" />
              }
              onPress={() => setMode("create")}
            />
          </div>
        ) : (
          <div className="flex flex-col w-full">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-coval-border">
              <Tooltip content="Back">
                <button
                  type="button"
                  onClick={() => setMode("list")}
                  className="p-1 -ml-1 rounded hover:bg-surface-1 transition-colors"
                  aria-label="Back"
                >
                  <ArrowLeft className="w-4 h-4 text-coval-text-muted" />
                </button>
              </Tooltip>
              <span className="text-sm font-medium">New attribute</span>
            </div>
            <div className="flex flex-col gap-3 p-3">
              <div>
                <Label htmlFor={nameInputId}>Name</Label>
                <Input
                  id={nameInputId}
                  placeholder="e.g. customer_tier"
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  error={isDuplicate}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && canSubmit) {
                      e.preventDefault()
                      handleAddMetadata()
                    }
                  }}
                  ref={focusOnMount}
                  aria-label="New column name"
                />
                {isDuplicate && (
                  <p className="text-xs text-coval-danger-text mt-1">
                    Column already exists
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor={typeSelectId}>Type</Label>
                <Select
                  value={type}
                  onValueChange={(val) => setType(val as MetadataColumnType)}
                >
                  <SelectTrigger id={typeSelectId} aria-label="New column type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPE_OPTIONS.map((t) => (
                      <SelectItem key={t} value={t}>
                        <span className="flex items-center gap-2">
                          {React.createElement(METADATA_TYPE_ICONS[t], {
                            className: "w-4 h-4 text-coval-text-muted",
                          })}
                          {METADATA_COLUMN_TYPE_LABELS[t]}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                disabled={!canSubmit}
                onClick={handleAddMetadata}
                className="self-stretch"
              >
                Create attribute
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
